import React from "react";

import validate from "../utilities/validate";
import Label from "./label.jsx";
/**
 * @typedef {import('../../types').showValidation} showValidation
 */

/**
 * @typedef {object} RadioButtonsProps - The props for the component
 * @property {string} componentId - The id of the component
 * @property {Array<{ label: string, responseId: string }>} items - The items to render
 * @property {object} validation - The validation for the form element
 * @property {showValidation} showValidation - Check the validation
 */

/**
 * Render a Radio Button Group where only one
 * radio button can be checked at any one point
 * @type {React.FC<RadioButtonsProps>}
 * @returns {React.ReactElement} React function component
 */
function RadioButtons({ component, showValidation }) {
  const { responses, componentId, validation } = component;
  /**
   *
   * @param {React.SyntheticEvent<HTMLInputElement>} event Synthetic event
   * @returns {void}
   */
  function updateValidation(event) {
    const { value } = event.currentTarget;
    const { isValid, key = "" } = validate("radiobuttons", value, validation);
    showValidation(isValid, key, value);
  }

  return (
    <div className="fr-mb-4">
      {component.label && (
        <legend className="fr-mb-3 fr-text-lg">
          {component.label}
          {validation?.required && (
            <>
              <span className="fr-text-red-600">*</span>
              <span className="fr-sr-only">(This field is required)</span>
            </>
          )}
        </legend>
      )}
      {responses.map(({ label, responseId }) => (
        <div
          key={`${componentId}-${responseId}`}
          className="fr-flex fr-items-center fr-mb-2"
        >
          <input
            type="radio"
            value={responseId}
            id={`${componentId}-${responseId}`}
            name={`form-component-${componentId}`}
            onChange={(event) => updateValidation(event)}
            className="fr-h-4 fr-w-4 fr-border-zinc-300 fr-text-primary-600 focus-visible:fr-ring-primary-500"
          />
          {label && (
            <div className="fr-pl-2">
              <Label value={label} htmlFor={`${componentId}-${responseId}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default RadioButtons;
